/**
 * LibraryFilterBar Component
 * Renders Tassie suitcase with 3 filter icons (Read, Play, Music)
 * Only visible when the user is on the library page
 */

import { t } from '../i18n/config.js';

/**
 * Filter definitions mapping filter names to activity types
 */
const FILTERS = [
  {
    id: 'read',
    icon: '/svg/icons/LeesIconGraphic.svg',
    ariaLabel: () => t('child.library.filterRead') || 'Read',
    types: ['lees', 'story'],
  },
  {
    id: 'play',
    icon: '/svg/icons/SpeelIcon.svg',
    ariaLabel: () => t('child.library.filterPlay') || 'Play',
    types: ['word-search', 'speel', 'memory'],
  },
  {
    id: 'music',
    icon: '/svg/icons/SingIcon.svg',
    ariaLabel: () => t('child.library.filterMusic') || 'Music',
    types: ['sing'],
  },
];

export class LibraryFilterBar {
  constructor() {
    this.element = null;
    this.activeFilter = null;
    this.profileId = null;
    this._scrollHandler = null;
    this._scrollDebounceTimer = null;
  }

  /**
   * Set the profile ID for localStorage scoping
   * @param {string} profileId
   */
  setProfileId(profileId) {
    this.profileId = profileId;
  }

  /**
   * Render the filter bar into the given container
   * @param {HTMLElement} container - The navigation bar element to render into
   */
  render(container) {
    if (this.element) {
      this.remove();
    }

    const filterBar = document.createElement('div');
    filterBar.className = 'library-filter-bar';
    filterBar.setAttribute('role', 'toolbar');
    filterBar.setAttribute('aria-label', t('child.library.filterToolbar') || 'Content filters');

    // Tassie suitcase background
    const tassie = document.createElement('div');
    tassie.className = 'library-filter-bar__tassie';
    tassie.innerHTML = `<img src="/svg/components/Tassie-1.svg" alt="" class="library-filter-bar__tassie-img" />`;
    filterBar.appendChild(tassie);

    // Filter icons container
    const iconsContainer = document.createElement('div');
    iconsContainer.className = 'library-filter-bar__icons';

    FILTERS.forEach((filter) => {
      const button = document.createElement('button');
      button.className = 'library-filter-bar__icon';
      button.dataset.filter = filter.id;
      button.setAttribute('aria-label', filter.ariaLabel());
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = `<img src="${filter.icon}" alt="" class="library-filter-bar__icon-img" />`;

      button.addEventListener('click', () => this.handleFilterClick(filter.id));
      iconsContainer.appendChild(button);
    });

    filterBar.appendChild(iconsContainer);

    // Insert in DOM order between the (hidden) library item and the achievements item,
    // so keyboard Tab order follows Profile -> Read -> Play -> Music -> Achievements -> Settings.
    // Relying on CSS `order` alone for this is unreliable across browsers when combined
    // with the settings icon's `position: absolute` — DOM order is the source of truth.
    const achievementsItem = container.querySelector('[data-route="achievements"]');
    if (achievementsItem) {
      container.insertBefore(filterBar, achievementsItem);
    } else {
      container.appendChild(filterBar);
    }
    this.element = filterBar;

    // Restore persisted filter
    this._restoreFilter();

    // Start scroll persistence
    this._initScrollPersistence();
  }

  /**
   * Handle filter icon click
   * @param {string} filterId - The filter that was clicked
   */
  handleFilterClick(filterId) {
    if (this.activeFilter === filterId) {
      // Deactivate current filter (show all)
      this.activeFilter = null;
      this.updateActiveState();
      this.applyFilter(null);
      this._clearStoredFilter();
    } else {
      // Activate new filter
      this.activeFilter = filterId;
      this.updateActiveState();
      this.applyFilter(filterId);
      this._storeFilter(filterId);
      // Reset scroll on filter change
      this._resetScroll();
    }
  }

  /**
   * Update visual active state on filter buttons
   */
  updateActiveState() {
    if (!this.element) return;

    const buttons = this.element.querySelectorAll('.library-filter-bar__icon');
    buttons.forEach((btn) => {
      const isActive = btn.dataset.filter === this.activeFilter;
      btn.classList.toggle('library-filter-bar__icon--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /**
   * Apply filter to library grid cards using CSS classes
   * @param {string|null} filterId - Filter to apply, or null to show all
   */
  applyFilter(filterId) {
    const grid = document.querySelector('.library-grid');
    if (!grid) return;

    const cards = grid.querySelectorAll('.library-grid__item');

    if (!filterId) {
      // Show all cards
      cards.forEach((card) => {
        card.classList.remove('library-grid__item--hidden');
      });
      return;
    }

    // Find the filter definition
    const filterDef = FILTERS.find((f) => f.id === filterId);
    if (!filterDef) return;

    cards.forEach((card) => {
      // Get the activity type from the card's content
      const contentCard = card.querySelector('.content-card');
      const activityType = contentCard?.dataset?.activityType || '';

      const matches = filterDef.types.includes(activityType.toLowerCase());
      card.classList.toggle('library-grid__item--hidden', !matches);
    });
  }

  /**
   * Remove the filter bar from the DOM
   */
  remove() {
    // Reset any active filters before removing
    if (this.activeFilter) {
      this.applyFilter(null);
      this.activeFilter = null;
    }

    // Remove scroll listener
    this._removeScrollPersistence();

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  /**
   * Check if filter bar is currently rendered
   * @returns {boolean}
   */
  isRendered() {
    return this.element !== null;
  }

  // ═══════════════════════════════════════════════════════════════
  // PERSISTENCE HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Store the active filter in localStorage
   */
  _storeFilter(filterId) {
    if (!this.profileId) return;
    try {
      localStorage.setItem(`kassie_library_filter_${this.profileId}`, filterId);
    } catch { /* ignore */ }
  }

  /**
   * Clear the stored filter from localStorage
   */
  _clearStoredFilter() {
    if (!this.profileId) return;
    try {
      localStorage.removeItem(`kassie_library_filter_${this.profileId}`);
    } catch { /* ignore */ }
  }

  /**
   * Restore the previously selected filter on render
   */
  _restoreFilter() {
    if (!this.profileId) return;
    try {
      const stored = localStorage.getItem(`kassie_library_filter_${this.profileId}`);
      if (stored && FILTERS.find(f => f.id === stored)) {
        this.activeFilter = stored;
        this.updateActiveState();
        // Delay filter application so cards are rendered first
        setTimeout(() => this.applyFilter(stored), 100);
      }
    } catch { /* ignore */ }
  }

  /**
   * Initialize scroll position persistence (debounced)
   */
  _initScrollPersistence() {
    // Restore scroll position after a short delay (cards need to render)
    setTimeout(() => {
      this._restoreScroll();
    }, 400);

    // Listen for scroll on window (the page itself scrolls, not a container)
    this._scrollHandler = () => {
      if (this._scrollDebounceTimer) clearTimeout(this._scrollDebounceTimer);
      this._scrollDebounceTimer = setTimeout(() => {
        this._storeScroll(window.scrollY);
      }, 300);
    };

    window.addEventListener('scroll', this._scrollHandler);
  }

  /**
   * Remove scroll listener
   */
  _removeScrollPersistence() {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
    }
    this._scrollHandler = null;
    if (this._scrollDebounceTimer) {
      clearTimeout(this._scrollDebounceTimer);
      this._scrollDebounceTimer = null;
    }
  }

  /**
   * Store scroll position
   */
  _storeScroll(y) {
    if (!this.profileId) return;
    try {
      localStorage.setItem(`kassie_library_scroll_${this.profileId}`, String(y));
    } catch { /* ignore */ }
  }

  /**
   * Restore scroll position from localStorage
   */
  _restoreScroll() {
    if (!this.profileId) return;
    try {
      const stored = localStorage.getItem(`kassie_library_scroll_${this.profileId}`);
      if (stored) {
        const y = parseInt(stored, 10);
        if (!isNaN(y) && y > 0) {
          window.scrollTo(0, y);
        }
      }
    } catch { /* ignore */ }
  }

  /**
   * Reset scroll to top and clear stored position
   */
  _resetScroll() {
    window.scrollTo(0, 0);
    if (!this.profileId) return;
    try {
      localStorage.removeItem(`kassie_library_scroll_${this.profileId}`);
    } catch { /* ignore */ }
  }
}
